// ContratTravail.jsx – Version corrigée avec capture complète PDF
import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf, faDownload, faTimes, faSpinner, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';
import defaultLogo from '../../../assetes/logos.png';

const API_URL = `${API_CONFIG2.apiUrl}employe/api_attestation.php`;

const ContratTravail = ({ employeId }) => {
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [contratData, setContratData] = useState(null);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const contratRef = useRef(null);
  const modalBodyRef = useRef(null); // Pour gérer le scroll du modal

  useEffect(() => {
    if (employeId) loadData();
  }, [employeId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}?employe_id=${employeId}`);
      const json = await res.json();
      if (json.status === 'success') {
        setContratData(json.data);
      } else {
        alert('Erreur : ' + (json.message || 'Données non trouvées'));
      }
    } catch (err) {
      alert('Erreur réseau : ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!contratRef.current) return;
    setGeneratingPDF(true);

    try {
      const element = contratRef.current;
      const modalBody = modalBodyRef.current;

      // Sauvegarder les styles originaux
      const originalModalOverflow = modalBody ? modalBody.style.overflow : '';
      const originalElementOverflow = element.style.overflow;
      const originalElementHeight = element.style.height;
      const originalElementMaxHeight = element.style.maxHeight;

      // 1. Désactiver le scroll du modal pour éviter les coupures
      if (modalBody) {
        modalBody.style.overflow = 'visible';
      }

      // 2. Permettre à l'élément de prendre toute sa hauteur
      element.style.overflow = 'visible';
      element.style.height = 'auto';
      element.style.maxHeight = 'none';

      // 3. Forcer le reflow
      element.scrollTop = 0;

      // 4. Capturer avec html2canvas
      const canvas = await html2canvas(element, {
        scale: 2.5,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
        allowTaint: false,
        // Utiliser les dimensions réelles du contenu
        windowHeight: element.scrollHeight,
        windowWidth: element.scrollWidth,
        onclone: (clonedDoc) => {
          // S'assurer que le clone est bien visible
          const clonedEl = clonedDoc.querySelector('[data-contrat]');
          if (clonedEl) {
            clonedEl.style.overflow = 'visible';
            clonedEl.style.height = 'auto';
            clonedEl.style.maxHeight = 'none';
          }
        }
      });

      // 5. Restaurer les styles
      if (modalBody) {
        modalBody.style.overflow = originalModalOverflow || 'auto';
      }
      element.style.overflow = originalElementOverflow || '';
      element.style.height = originalElementHeight || '';
      element.style.maxHeight = originalElementMaxHeight || '';

      // 6. Générer le PDF
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      // Si le contenu tient sur une page, ajouter directement, sinon gérer le découpage
      if (pdfHeight <= pdf.internal.pageSize.getHeight()) {
        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      } else {
        // Découpage en plusieurs pages si nécessaire
        const pageHeight = pdf.internal.pageSize.getHeight();
        const ratio = canvas.width / canvas.height;
        let remainingHeight = canvas.height;
        let yOffset = 0;

        while (remainingHeight > 0) {
          const currentPageHeight = Math.min(remainingHeight, canvas.height * (pageHeight / pdfHeight));
          const canvasPageHeight = currentPageHeight;
          const canvasPageWidth = canvasPageHeight * ratio;

          // Créer un canvas temporaire pour la page
          const pageCanvas = document.createElement('canvas');
          pageCanvas.width = canvas.width;
          pageCanvas.height = canvasPageHeight;
          const ctx = pageCanvas.getContext('2d');
          ctx.drawImage(canvas, 0, yOffset, canvas.width, canvasPageHeight, 0, 0, canvas.width, canvasPageHeight);

          const pageImgData = pageCanvas.toDataURL('image/png');
          const pagePdfWidth = pdfWidth;
          const pagePdfHeight = (pageCanvas.height * pagePdfWidth) / pageCanvas.width;

          if (yOffset > 0) pdf.addPage();
          pdf.addImage(pageImgData, 'PNG', 0, 0, pagePdfWidth, pagePdfHeight);

          remainingHeight -= canvasPageHeight;
          yOffset += canvasPageHeight;
        }
      }

      pdf.save(`Contrat_${contratData.employe.nom || 'employe'}.pdf`);
    } catch (error) {
      console.error('Erreur PDF:', error);
      alert('Erreur lors de la génération du PDF : ' + error.message);
    } finally {
      setGeneratingPDF(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatDateLong = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  const employe = contratData?.employe;
  const entreprise = contratData?.entreprise || {};
  const logoUrl = entreprise.logoUrl || defaultLogo;

  const typeContrat = employe?.type_contrat || 'CDI';
  const dateDebut = employe?.date_debut_contrat || employe?.date_embauche;
  const dateFin = employe?.date_fin_contrat;
  const periodeEssai = '3 mois';
  const numeroContrat = `CT-${String(employe?.id || '').padStart(4, '0')}-${new Date().getFullYear()}`;

  return (
    <>
      {/* Bouton d'ouverture */}
      <button
        className="btn-contrat"
        onClick={() => setShowModal(true)}
        disabled={loading}
        style={{
          padding: '12px 28px',
          borderRadius: '40px',
          border: 'none',
          background: 'linear-gradient(135deg, #1e40af, #1e3a8a)',
          color: '#fff',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '10px',
          transition: 'all 0.3s ease',
          fontWeight: '600',
          fontSize: '0.95rem',
          boxShadow: '0 4px 14px rgba(30, 64, 175, 0.35)',
        }}
        onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
        onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
      >
        {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faFilePdf} />}
        {loading ? 'Chargement...' : '📄 Contrat de travail'}
      </button>

      {/* Modal */}
      {showModal && contratData && (
        <div className="contrat-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="contrat-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="contrat-modal-header">
              <h3>
                <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#22c55e', marginRight: '8px' }} />
                Contrat de travail
              </h3>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="contrat-modal-body" ref={modalBodyRef}>
              <div className="contrat-paper" ref={contratRef} data-contrat>
                {/* En-tête avec logo */}
                <div className="contrat-header">
               
                  <div className="header-right">
                    <img src={logoUrl} alt="Logo" className="logo-img" crossOrigin="anonymous" />
                    <div className="coords">
                      <p>{entreprise.adresse || ''}</p>
                      <p>
                        {entreprise.telephone && <span>Tél: {entreprise.telephone}</span>}
                        {entreprise.email && <span> | Email: {entreprise.email}</span>}
                      </p>
                      <p className="small">
                        {entreprise.rccm && <span>RCCM: {entreprise.rccm}</span>}
                        {entreprise.idnat && <span> | ID.NAT: {entreprise.idnat}</span>}
                        {entreprise.nimpot && <span> | N° Impôt: {entreprise.nimpot}</span>}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="separator"></div>

                <div className="reference">
                  <span>N° Contrat : <strong>{numeroContrat}</strong></span>
                  <span>Date : <strong>{formatDateLong(new Date())}</strong></span>
                </div>

                <h2 className="title">CONTRAT DE TRAVAIL</h2>

                <div className="body-text">
                  <p><strong>ENTRE LES SOUSSIGNÉS :</strong></p>
                  <p>
                    <strong>{entreprise.nom || 'l\'entreprise'}</strong>, société dont le siège social est situé à {entreprise.adresse || 'Kinshasa'},
                    immatriculée au RCCM sous le numéro {entreprise.rccm || '...'}, représentée par son Gérant,
                    ci-après dénommée <strong>l'Employeur</strong>.
                  </p>
                  <p><strong>ET</strong></p>
                  <p>
                    <strong>{employe.prenom} {employe.nom} {employe.postnom || ''}</strong>, né(e) le <strong>{formatDate(employe.date_naissance)}</strong>
                    à <strong>{employe.lieu_naissance || '...'}</strong>, de nationalité <strong>{employe.nationalite || 'congolaise'}</strong>,
                    résidant à <strong>{employe.ville || 'Kinshasa'}</strong>, commune de <strong>{employe.commune || '...'}</strong>,
                    {employe.adresse && <span> , {employe.adresse}</span>},
                    ci-après dénommé(e) <strong>le Salarié</strong>.
                  </p>

                  <div className="article">
                    <h4>Article 1 – Objet du contrat</h4>
                    <p>
                      Le présent contrat a pour objet d'établir les relations de travail entre l'Employeur et le Salarié,
                      conformément aux dispositions de la législation en vigueur.
                    </p>
                  </div>

                  <div className="article">
                    <h4>Article 2 – Fonctions</h4>
                    <p>
                      Le Salarié est engagé(e) en qualité de <strong>{employe.fonction || 'Non spécifié'}</strong>
                      au sein du service <strong>{employe.service || 'Non spécifié'}</strong>.
                      Il/Elle exercera ses fonctions sous l'autorité et la direction du responsable hiérarchique.
                    </p>
                  </div>

                  <div className="article">
                    <h4>Article 3 – Durée et période d'essai</h4>
                    <p>
                      Le présent contrat est conclu pour une durée <strong>{typeContrat}</strong>.
                      Il prend effet à compter du <strong>{formatDate(dateDebut)}</strong>
                      {dateFin && <span> et se termine le <strong>{formatDate(dateFin)}</strong></span>}.
                    </p>
                    <p>
                      Une période d'essai de <strong>{periodeEssai}</strong> est prévue, durant laquelle chaque partie
                      peut résilier le contrat sans indemnité, sous réserve d'un préavis de 48 heures.
                    </p>
                  </div>

                  <div className="article">
                    <h4>Article 4 – Rémunération</h4>
                    <p>
                      En contrepartie de son travail, le Salarié percevra un salaire mensuel brut de <strong>
                        {Number(employe.salaire_base).toLocaleString('fr-FR')} CDF
                      </strong>, payable au plus tard le 5 du mois suivant.
                    </p>
                    <p>
                      Ce salaire sera versé par virement bancaire sur le compte {employe.banque || '...'} n° {employe.compte_bancaire || '...'}.
                    </p>
                  </div>

                  <div className="article">
                    <h4>Article 5 – Lieu de travail</h4>
                    <p>
                      Le Salarié exercera ses fonctions au siège social de l'entreprise situé à {entreprise.adresse || 'Kinshasa'}.
                      Des déplacements occasionnels pourront être requis.
                    </p>
                  </div>

                  <div className="article">
                    <h4>Article 6 – Obligations du Salarié</h4>
                    <p>
                      Le Salarié s'engage à :<br />
                      – Exécuter loyalement les tâches confiées ;<br />
                      – Respecter le règlement intérieur et les consignes de sécurité ;<br />
                      – Observer une stricte discrétion professionnelle.
                    </p>
                  </div>

                  <div className="article">
                    <h4>Article 7 – Obligations de l'Employeur</h4>
                    <p>
                      L'Employeur s'engage à :<br />
                      – Fournir un travail conforme à la qualification du Salarié ;<br />
                      – Payer le salaire convenu ;<br />
                      – Assurer la sécurité et la santé du Salarié sur le lieu de travail.
                    </p>
                  </div>

                  <div className="article">
                    <h4>Article 8 – Résiliation</h4>
                    <p>
                      Le contrat pourra être résilié par l'une ou l'autre des parties, moyennant un préavis de
                      <strong> {typeContrat === 'CDI' ? '30 jours' : '15 jours'}</strong>.
                      En cas de faute grave, la rupture sera immédiate.
                    </p>
                  </div>

                  <div className="article">
                    <h4>Article 9 – Dispositions finales</h4>
                    <p>
                      Les parties s'engagent à respecter les clauses du présent contrat et les dispositions légales
                      en vigueur, notamment celles relatives au droit du travail et à la sécurité sociale.
                    </p>
                    <p>
                      Les litiges éventuels seront soumis aux juridictions compétentes de la République Démocratique du Congo.
                    </p>
                  </div>

                  <p className="clause">
                    Fait à Kinshasa, le {formatDateLong(new Date())}, en deux exemplaires originaux, chaque partie reconnaît en avoir reçu un.
                  </p>
                </div>

                <div className="signature-block">
                  <div className="signature-left">
                    <p><strong>Le Salarié</strong></p>
                    <div className="signature-line"></div>
                    <p className="signature-caption">Signature précédée de la mention "Lu et approuvé"</p>
                  </div>
                  <div className="signature-right">
                    <p><strong>L'Employeur</strong></p>
                    {entreprise.signatureUrl && (
                      <img src={entreprise.signatureUrl} alt="Signature" className="signature-img" crossOrigin="anonymous" />
                    )}
                    <div className="signature-line"></div>
                    <p className="signature-caption">Signature et cachet de l'entreprise</p>
                  </div>
                </div>

                <div className="footer">
                  {entreprise.nom} – {entreprise.adresse} – Tél: {entreprise.telephone}
                  <span>Page 1/1</span>
                </div>
              </div>
            </div>

            <div className="contrat-modal-footer">
              <button className="btn-secondary" onClick={() => setShowModal(false)}>Fermer</button>
              <button className="btn-primary" onClick={handleDownloadPDF} disabled={generatingPDF}>
                {generatingPDF ? (
                  <><FontAwesomeIcon icon={faSpinner} spin /> Génération...</>
                ) : (
                  <><FontAwesomeIcon icon={faDownload} /> Télécharger PDF</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Styles CSS – inchangés, mais on ajoute une règle pour le conteneur pendant la capture */}
      <style jsx>{`
        .contrat-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.6);
          backdrop-filter: blur(8px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          padding: 20px;
          animation: fadeIn 0.3s ease;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.98); }
          to { opacity: 1; transform: scale(1); }
        }
        .contrat-modal-content {
          background: #ffffff;
          border-radius: 28px;
          max-width: 880px;
          width: 100%;
          max-height: 100%;
          display: flex;
          flex-direction: column;
          box-shadow: 0 40px 100px rgba(0, 0, 0, 0.4);
          overflow: hidden;
        }
        .contrat-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 18px 28px;
          background: #f8fafc;
          border-bottom: 1px solid #e2e8f0;
          flex-shrink: 0;
        }
        .contrat-modal-header h3 {
          margin: 0;
          font-size: 1.2rem;
          color: #0f172a;
          font-weight: 600;
          display: flex;
          align-items: center;
        }
        .close-btn {
          background: none;
          border: none;
          font-size: 1.6rem;
          cursor: pointer;
          color: #94a3b8;
          transition: 0.2s;
          padding: 0 4px;
        }
        .close-btn:hover {
          color: #ef4444;
          transform: rotate(90deg);
        }
        .contrat-modal-body {
          flex: 1;
          overflow-y: auto;
          padding: 20px 24px 16px;
          background: #f1f5f9;
          display: flex;
          justify-content: center;
          transition: overflow 0.3s;
        }
        .contrat-paper {
          max-width: 750px;
          width: 100%;
          padding: 40px 44px;
          background: #ffffff;
          border-radius: 16px;
          box-shadow: 0 4px 24px rgba(0, 0, 0, 0.04);
          font-family: 'Georgia', 'Times New Roman', serif;
          color: #1e293b;
          line-height: 1.7;
          transition: all 0.2s;
          border: 1px solid #e2e8f0;
          position: relative;
        }
        .contrat-paper::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23f1f5f9' fill-opacity='0.3'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
          pointer-events: none;
          opacity: 0.3;
        }
        .contrat-header {
          display: flex;
          align-items: center;
          gap: 24px;
          margin-bottom: 18px;
          position: relative;
          z-index: 1;
        }
        .header-left {
          flex-shrink: 0;
        }
        .logo-img {
          max-width: 110px;
          max-height: 80px;
          object-fit: contain;
        }
        .header-right {
          flex: 1;
          text-align: left;
        }
        .header-right h1 {
          font-size: 22px;
          font-weight: 700;
          margin: 0 0 4px 0;
          color: #0f172a;
          letter-spacing: 0.5px;
        }
        .coords {
          font-size: 12px;
          color: #475569;
          margin: 2px 0;
        }
        .coords p {
          margin: 1px 0;
        }
        .coords .small {
          font-size: 11px;
          color: #64748b;
        }
        .separator {
          height: 3px;
          background: linear-gradient(to right, #1e3a8a, #3b82f6, #1e3a8a);
          border-radius: 4px;
          margin: 16px 0 22px;
          position: relative;
          z-index: 1;
        }
        .reference {
          display: flex;
          justify-content: space-between;
          font-size: 12px;
          color: #475569;
          margin-bottom: 16px;
          position: relative;
          z-index: 1;
        }
        .title {
          text-align: center;
          font-size: 24px;
          font-weight: 700;
          color: #0f172a;
          letter-spacing: 2px;
          margin: 8px 0 28px;
          text-transform: uppercase;
          position: relative;
          z-index: 1;
        }
        .title::after {
          content: '';
          display: block;
          width: 80px;
          height: 3px;
          background: #1e3a8a;
          margin: 8px auto 0;
          border-radius: 2px;
        }
        .body-text {
          font-size: 11px;
          text-align: justify;
          color: #1e293b;
          position: relative;
          z-index: 1;
        }
        .body-text p {
          margin: 0 0 10px 0;
        }
        .body-text strong {
          color: #0f172a;
        }
        .article {
          margin-top: 35px;
          border-top: 1px dashed #e2e8f0;
          padding-top: 14px;
        }
        .article h4 {
          font-size: 14px;
          font-weight: 700;
          color: #1e3a8a;
          margin: 0 0 6px 0;
        }
        .clause {
          font-style: italic;
          color: #334155;
          margin-top: 12px;
          border-left: 3px solid #3b82f6;
          padding-left: 16px;
        }
        .signature-block {
          margin-top: 38px;
          display: flex;
          justify-content: space-between;
          gap: 40px;
          text-align: center;
          position: relative;
          z-index: 1;
        }
        .signature-block > div {
          flex: 1;
          min-width: 120px;
        }
        .signature-img {
          max-width: 160px;
          max-height: 60px;
          object-fit: contain;
          display: block;
          margin: 4px auto 6px;
        }
        .signature-line {
          width: 100%;
          max-width: 180px;
          height: 1px;
          background: #94a3b8;
          margin: 6px auto 2px;
        }
        .signature-caption {
          font-size: 11px;
          color: #64748b;
          margin-top: 2px;
        }
        .footer {
          margin-top: 34px;
          border-top: 1px solid #e2e8f0;
          padding-top: 16px;
          font-size: 11px;
          color: #94a3b8;
          text-align: center;
          letter-spacing: 0.3px;
          position: relative;
          z-index: 1;
        }
        .footer span {
          float: right;
        }
        .contrat-modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 14px;
          padding: 16px 28px;
          background: #f8fafc;
          border-top: 1px solid #e2e8f0;
          flex-shrink: 0;
          flex-wrap: wrap;
        }
        .btn-primary, .btn-secondary {
          padding: 10px 24px;
          border-radius: 40px;
          border: none;
          cursor: pointer;
          font-weight: 600;
          transition: all 0.25s ease;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-size: 0.9rem;
        }
        .btn-primary {
          background: #3b82f6;
          color: #fff;
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
        }
        .btn-primary:hover:not(:disabled) {
          background: #1d4ed8;
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
        }
        .btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }
        .btn-secondary {
          background: #e2e8f0;
          color: #1e293b;
        }
        .btn-secondary:hover {
          background: #cbd5e1;
          transform: translateY(-2px);
        }
        @media (max-width: 640px) {
          .contrat-modal-content {
            max-height: 100vh;
            border-radius: 0;
            max-width: 100%;
          }
          .contrat-modal-body {
            padding: 12px;
          }
          .contrat-paper {
            padding: 24px 16px;
          }
          .contrat-header {
            flex-direction: column;
            text-align: center;
            gap: 12px;
          }
          .header-right {
            text-align: center;
          }
          .header-right h1 {
            font-size: 18px;
          }
          .title {
            font-size: 20px;
          }
          .signature-block {
            flex-direction: column;
            gap: 20px;
          }
          .contrat-modal-footer {
            flex-direction: column-reverse;
          }
          .btn-primary, .btn-secondary {
            justify-content: center;
            width: 100%;
          }
          .reference {
            flex-direction: column;
            align-items: center;
            gap: 4px;
          }
          .footer span {
            float: none;
            display: block;
            margin-top: 4px;
          }
        }
      `}</style>
    </>
  );
};

export default ContratTravail;